<template>
  <div class="generator">
    <!-- 1. Password card -->
    <Card class="gap-4">
      <CardHeader class="pb-2">
        <CardTitle class="text-base">{{ $t('generator.yourPassword') }}</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <TooltipProvider :delay-duration="0">
          <Tooltip :open="copySuccess" @update:open="(v) => !v && (copySuccess = false)">
            <TooltipTrigger as-child>
              <div
                role="button"
                tabindex="0"
                :aria-label="generatedPassword ? $t('aria.passwordGenerated') : $t('aria.noPassword')"
                class="flex min-h-20 w-full flex-col justify-center rounded-lg border border-border bg-primary px-4 py-3 text-primary-foreground"
                :class="{ 'cursor-pointer hover:bg-primary/90': generatedPassword, 'cursor-default opacity-90': !generatedPassword }"
                @click="generatedPassword && copyToClipboard()"
                @keydown.enter="generatedPassword && copyToClipboard()"
              >
                <span class="font-mono text-sm tracking-wide break-all">{{ generatedPassword || $t('generator.selectOneType') }}</span>
                <span v-if="generatedPassword" class="mt-1 text-xs opacity-85">{{ $t('generator.clickToCopy') }}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="font-medium">
              {{ $t('generator.copied') }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div v-if="generatedPassword" class="space-y-2">
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-medium text-foreground">{{ $t(strengthCategoryKey) }}</span>
            <span class="text-xs text-muted-foreground">{{ passwordStrengthLabel }}</span>
          </div>
          <div :class="['progress-strength-wrap', progressStrengthClass]">
            <Progress :model-value="passwordStrengthPercentage" :max="100" class="h-2 flex-1" />
          </div>
        </div>

        <Button class="w-full" @click="generatePassword">
          <RefreshCwIcon class="size-4" />
          {{ $t('generator.regenerate') }}
        </Button>
      </CardContent>
    </Card>

    <!-- 2. Opzioni card -->
    <Card class="mt-4">
      <CardHeader class="pb-2">
        <CardTitle class="text-base">{{ $t('generator.options') }}</CardTitle>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="space-y-2">
          <Label for="custom-key" class="text-sm font-medium">{{ $t('generator.customKey') }}</Label>
          <input
            id="custom-key"
            v-model.trim="customKey"
            type="text"
            :placeholder="$t('generator.customKeyPlaceholder')"
            class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            autocomplete="off"
            @input="onCustomKeyInput"
          />
          <p class="text-xs text-muted-foreground">{{ $t('generator.customKeyHint') }}</p>
        </div>

        <div class="space-y-2">
          <Label for="slider-length" class="text-sm font-medium">
            {{ $t('generator.length') }}: <strong>{{ passwordLength }}</strong> {{ $t('generator.characters') }}
          </Label>
          <Slider
            id="slider-length"
            :min="8"
            :max="64"
            :step="1"
            :model-value="[passwordLength]"
            @update:model-value="(v) => passwordLength = v[0]"
          />
        </div>

        <div class="space-y-2">
          <Label class="text-sm font-medium">{{ $t('generator.includeChars') }}</Label>
          <div class="flex flex-col gap-3">
            <label class="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <Switch id="uppercase" v-model="includeUppercase" />
              <span>{{ $t('generator.uppercase') }}</span>
            </label>
            <label class="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <Switch id="lowercase" v-model="includeLowercase" />
              <span>{{ $t('generator.lowercase') }}</span>
            </label>
            <label class="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <Switch id="numbers" v-model="includeNumbers" />
              <span>{{ $t('generator.numbers') }}</span>
            </label>
            <label class="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <Switch id="special" v-model="includeSpecial" />
              <span>{{ $t('generator.symbols') }}</span>
            </label>
            <label class="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <Switch id="no-ambiguous" v-model="excludeAmbiguous" />
              <span>{{ $t('generator.excludeAmbiguous') }}</span>
            </label>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { RefreshCwIcon } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export default {
  name: 'PasswordGenerator',
  components: {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Label,
    Progress,
    RefreshCwIcon,
    Slider,
    Switch,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
  },
  data() {
    return {
      passwordLength: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeSpecial: true,
      includeNumbers: true,
      excludeAmbiguous: false,
      customKey: '',
      generatedPassword: '',
      passwordStrength: 0,
      copySuccess: false,
      customKeyDebounce: null
    }
  },
  computed: {
    passwordStrengthPercentage() {
      return Math.min(100, Math.round(this.passwordStrength))
    },
    strengthCategoryKey() {
      const p = this.passwordStrengthPercentage
      if (p < 25) return 'generator.strengthWeak'
      if (p < 50) return 'generator.strengthMedium'
      if (p < 75) return 'generator.strengthStrong'
      return 'generator.strengthMax'
    },
    passwordStrengthLabel() {
      const pwd = this.generatedPassword
      if (!pwd) return ''
      const len = pwd.length
      const hasUpper = /[A-Z]/.test(pwd)
      const hasLower = /[a-z]/.test(pwd)
      const hasNumber = /\d/.test(pwd)
      const hasSpecial = /[^A-Za-z0-9]/.test(pwd)
      const types = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length
      return `${len} ${this.$t('generator.characters')}, ${types} ${this.$t('generator.types')}`
    },
    progressStrengthClass() {
      const p = this.passwordStrengthPercentage
      if (p < 25) return 'progress-strength-debole'
      if (p < 50) return 'progress-strength-media'
      if (p < 75) return 'progress-strength-forte'
      return 'progress-strength-massima'
    }
  },
  watch: {
    passwordLength: 'generatePassword',
    includeUppercase: 'generatePassword',
    includeLowercase: 'generatePassword',
    includeSpecial: 'generatePassword',
    includeNumbers: 'generatePassword',
    excludeAmbiguous: 'generatePassword'
  },
  methods: {
    calculatePasswordStrength() {
      const pwd = this.generatedPassword
      if (!pwd) {
        this.passwordStrength = 0
        return
      }
      const len = pwd.length
      const has = (re) => re.test(pwd)
      const hasUpper = has(/[A-Z]/)
      const hasLower = has(/[a-z]/)
      const hasNumber = has(/\d/)
      const hasSpecial = has(/[^A-Za-z0-9]/)
      const types = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length

      // Punteggio lunghezza (max 50): più lunga = più sicura, scale generosa per password lunghe
      let lengthScore = 0
      if (len <= 8) lengthScore = 8
      else if (len <= 10) lengthScore = 15
      else if (len <= 12) lengthScore = 22
      else if (len <= 16) lengthScore = 32
      else if (len <= 20) lengthScore = 40
      else if (len <= 28) lengthScore = 45
      else lengthScore = 50 // 29+ caratteri

      // Punteggio varietà (max 30): 1 tipo=0, 2=10, 3=20, 4=30
      const varietyScore = types === 1 ? 0 : types === 2 ? 10 : types === 3 ? 20 : 30

      // Bonus tutti e 4 i tipi (max 20)
      const allFourBonus = (hasUpper && hasLower && hasNumber && hasSpecial) ? 20 : 0

      let score = lengthScore + varietyScore + allFourBonus

      // Cap solo per password molto corte: 8 char max 35%, 10 char max 50%
      if (len <= 8) score = Math.min(score, 35)
      else if (len <= 10) score = Math.min(score, 50)

      this.passwordStrength = Math.min(100, score)
    },
    generatePassword() {
      if (!this.includeUppercase && !this.includeLowercase && !this.includeSpecial && !this.includeNumbers) {
        this.generatedPassword = ''
        this.passwordStrength = 0
        return
      }

      // Charset solo con i tipi selezionati (ordine fisso per evitare bug)
      let charset = ''
      if (this.includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      if (this.includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz'
      if (this.includeNumbers) charset += '0123456789'
      if (this.includeSpecial) charset += '!@#$%^&*()_+[]{}|;:,.<>?'

      // Escludi caratteri ambigui (0,O, 1,l,I, 5,S, 8,B, 2,Z, 6,G, etc.)
      const ambiguous = '0O1lI5S8B2Z6Gq9'
      if (this.excludeAmbiguous) {
        charset = charset.split('').filter((c) => !ambiguous.includes(c)).join('')
      }
      if (charset.length === 0) {
        this.generatedPassword = ''
        this.passwordStrength = 0
        return
      }

      const len = this.passwordLength
      const key = this.customKey

      if (key && key.length > 0) {
        // Chiave come derivazione: PBKDF2 con salt dalle opzioni → stessa chiave + stesse opzioni = stessa password
        this.derivePasswordFromKey(charset, len).then((password) => {
          this.generatedPassword = password.slice(0, 64)
          this.calculatePasswordStrength()
        })
        return
      }

      const randomValues = new Uint32Array(len)
      crypto.getRandomValues(randomValues)

      let password = ''
      for (let i = 0; i < len; i++) {
        password += charset[randomValues[i] % charset.length]
      }

      this.generatedPassword = password.slice(0, 64)
      this.calculatePasswordStrength()
    },
    async derivePasswordFromKey(charset, len) {
      const key = this.customKey
      if (!key || charset.length === 0) return ''

      const encoder = new TextEncoder()
      const keyBuffer = encoder.encode(key)
      const saltString = `pwgen-v1-${this.passwordLength}-${this.includeUppercase}-${this.includeLowercase}-${this.includeNumbers}-${this.includeSpecial}-${this.excludeAmbiguous}`
      const saltBuffer = encoder.encode(saltString)

      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        'PBKDF2',
        false,
        ['deriveBits']
      )

      const bitsLength = len * 8
      const derived = await crypto.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: saltBuffer,
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        bitsLength
      )

      const bytes = new Uint8Array(derived)
      let password = ''
      for (let i = 0; i < len; i++) {
        password += charset[bytes[i] % charset.length]
      }
      return password
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.generatedPassword)
      } catch {
        const el = document.createElement('textarea')
        el.value = this.generatedPassword
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.copySuccess = true
      setTimeout(() => {
        this.copySuccess = false
      }, 2000)
    },
    onCustomKeyInput() {
      if (this.customKeyDebounce) clearTimeout(this.customKeyDebounce)
      this.customKeyDebounce = setTimeout(() => {
        this.generatePassword()
        this.customKeyDebounce = null
      }, 400)
    }
  },
  created() {
    this.generatePassword()
  }
}
</script>

<style scoped>
.generator {
  max-width: 28rem;
  margin-left: auto;
  margin-right: auto;
  padding-bottom: 2rem;
}

/* Colore barra in base alla sicurezza: rosso → arancione → giallo → verde */
.progress-strength-wrap :deep([data-slot="progress-indicator"]) {
  transition: background-color 0.25s ease;
}
.progress-strength-debole :deep([data-slot="progress-indicator"]) {
  background-color: #dc2626;
}
.progress-strength-media :deep([data-slot="progress-indicator"]) {
  background-color: #ea580c;
}
.progress-strength-forte :deep([data-slot="progress-indicator"]) {
  background-color: #ca8a04;
}
.progress-strength-massima :deep([data-slot="progress-indicator"]) {
  background-color: #16a34a;
}
</style>
